import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Zap, Calendar, User, Clock, Share2, ArrowRight } from 'lucide-react';
import SEO from '../../components/layout/SEO';

interface PostContent {
    title: string;
    description: string;
    category: string;
    date: string;
    readTime: string;
    image: string;
    content: React.ReactNode;
}

const posts: Record<string, PostContent> = {
    'beneficios-previdenciarios-mei-2026': {
        title: "🛡️ Como declarar o novo benefício de previdência do MEI em 2026: Guia Completo.",
        description: "As regras mudaram e agora o MEI tem acesso a novos auxílios. Saiba como não perder seus direitos.",
        category: "Previdência",
        date: "19 de Março, 2026",
        readTime: "5 min de leitura",
        image: "/previdencia-mei-2026.png",
        content: (
            <>
                <p>Em 2026, a Previdência Social atualizou os benefícios para o Microempreendedor Individual. Além da aposentadoria por idade e salário-maternidade, agora o sistema de pontos foi simplificado. Mas atenção: para ter direito, o seu DAS deve estar rigorosamente em dia.</p>

                <p>Para declarar o recebimento de benefícios no seu Imposto de Renda ou na Declaração Anual (DASN-SIMEI), você deve separar o que é rendimento isento do que é tributável.</p>
                
                <h3>Dica de Ouro</h3>
                <p>Use o radar do Gestão MEI para monitorar seus pagamentos do DAS e garantir que sua carência previdenciária nunca seja interrompida por esquecimento.</p>

                <div className="mt-12 flex justify-center">
                    <Link 
                        to="/auth"
                        className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all hover:scale-105 flex items-center gap-3 shadow-lg shadow-red-600/20"
                    >
                        Quero meu Teste Grátis de 15 Dias
                        <Zap size={16} />
                    </Link>
                </div>
            </>
        )
    },
    'recuperacao-cnpj-suspenso': {
        title: "⚠️ Meu CNPJ foi suspenso por falta de faturamento: O que fazer agora?",
        description: "Não entre em pânico. Saiba como regularizar sua situação e evitar o cancelamento definitivo do seu registro.",
        category: "Regularização",
        date: "18 de Março, 2026",
        readTime: "4 min de leitura",
        image: "/cnpj-suspenso-alert.png",
        content: (
            <>
                <p>"Muitos empreendedores acreditam que, se não houver vendas, não é preciso declarar nada. Erro grave! A Receita Federal pode suspender o seu CNPJ por 'omissão de declarações'. Se o seu status está como 'Suspenso', o primeiro passo é entregar as Declarações Anuais (DASN) pendentes, mesmo que estejam zeradas.</p>

                <p>Após o envio, será gerada uma multa por atraso. Regularize os débitos e o seu CNPJ voltará à ativa em poucos dias.</p>
                
                <h3>Como evitar que aconteça de novo?</h3>
                <p>Tenha um sistema que te avise das obrigações. No Gestão MEI, nós notificamos você sobre todos os prazos críticos para que você foque em vender, enquanto nós cuidamos da burocracia.</p>

                <div className="mt-12 flex justify-center">
                    <Link 
                        to="/auth"
                        className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all hover:scale-105 flex items-center gap-3 shadow-lg shadow-red-600/20"
                    >
                        Quero meu Teste Grátis de 15 Dias
                        <Zap size={16} />
                    </Link>
                </div>
            </>
        )
    },
    'calculadora-preco-venda-lucro': {
        title: "📊 Calculadora de Preço de Vendas para MEI: Não confunda Faturamento com Lucro!",
        description: "Vender muito não significa ganhar dinheiro. Aprenda a precificar corretamente e veja a cor do seu lucro no final do mês.",
        category: "Elite",
        date: "17 de Março, 2026",
        readTime: "6 min de leitura",
        image: "/calculadora-lucro-venda.png",
        content: (
            <>
                <p>Você vende, vende e, no final do mês, a conta não fecha? O erro pode estar na precificação. Muitos MEIs somam o custo do produto e colocam uma 'margem' de 50%, esquecendo dos custos fixos, DAS, taxas de cartão e a reserva para o próprio salário (Pró-labore).</p>

                <p>Faturamento é tudo o que entra. Lucro é o que sobra depois de pagar TUDO e todos.</p>
                
                <h3>A solução:</h3>
                <p>Utilize a nossa calculadora interna no Gestão MEI para descobrir seu 'ponto de equilíbrio'. Nosso app separa automaticamente suas despesas pessoais das profissionais, mostrando a saúde real do seu negócio em tempo real.</p>

                <div className="mt-12 flex justify-center">
                    <Link 
                        to="/auth"
                        className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all hover:scale-105 flex items-center gap-3 shadow-lg shadow-red-600/20"
                    >
                        Quero meu Teste Grátis de 15 Dias
                        <Zap size={16} />
                    </Link>
                </div>
            </>
        )
    },
    'valor-gestao-profissional-mei': {
        title: "🔥 Por que 15 dias de gestão profissional podem salvar o seu ano?",
        description: "O caos financeiro é o maior inimigo do pequeno empresário. Descubra como a organização transforma o seu negócio.",
        category: "Gestão",
        date: "16 de Março, 2026",
        readTime: "4 min de leitura",
        image: "/gestao-profissional-fin.png",
        content: (
            <>
                <p>As estatísticas não mentem: a maioria das empresas que fecham no primeiro ano falham por falta de gestão financeira. Mas e se você tivesse um consultor digital por 15 dias, de graça?</p>

                <p>Estamos liberando o Plano Elite do Gestão MEI por 15 dias gratuitos. Nesse período, você vai escanear suas notas fiscais via QR Code, ver seu gráfico de lucro subir e entender exatamente para onde está indo cada centavo. Teste a tecnologia que grandes empresas usam, adaptada para o seu bolso de MEI. Não é apenas um app, é a sua sobrevivência no mercado.</p>

                <div className="mt-12 flex justify-center">
                    <Link 
                        to="/auth"
                        className="bg-red-600 hover:bg-red-500 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all hover:scale-105 flex items-center gap-3 shadow-lg shadow-red-600/20"
                    >
                        Quero meu Teste Grátis de 15 Dias
                        <Zap size={16} />
                    </Link>
                </div>
            </>
        )
    },
    'tecnologia-leitura-direta-nfce': {
        title: "Gestão MEI revoluciona: Lançamento de notas fiscais via QR Code agora com 'Tecnologia de Leitura Direta'",
        description: "Descubra como a nova tecnologia do Gestão MEI pula os Captchas dos sites do governo e extrai dados de notas fiscais em menos de 1 segundo.",
        category: "Inovação",
        date: "11 de Março, 2026",
        readTime: "3 min de leitura",
        image: "/scaneando.png",
        content: (
            <>
                <p>Organizar as despesas do MEI sempre foi uma tarefa manual e cansativa. Mas o Gestão MEI acaba de dar um salto tecnológico. Nossa nova função de escaneamento de NFC-e (Nota Fiscal de Consumidor) não apenas lê o código, mas utiliza uma inteligência que extrai o valor e a data diretamente do link oficial, pulando as barreiras dos sites do governo.</p>
                
                <h2>O Fim dos Captchas e das Esperas Lentas</h2>
                <p>Muitos Microempreendedores, especialmente no <strong>Amapá (AP)</strong> e em outros estados que utilizam sistemas de segurança robustos, enfrentavam um problema comum: ao tentar ler um QR Code, o site da SEFAZ exigia um "Captcha" (aquelas letras chatas para digitar). Isso quebrava a agilidade do lançamento.</p>
                
                <p>Nossa nova <strong>"Tecnologia de Leitura Direta"</strong> resolve isso de forma brilhante. Ao invés de depender exclusivamente do carregamento do site governamental, nosso algoritmo decodifica os parâmetros inseridos na própria URL do QR Code.</p>
                
                <h3>Como funciona na prática?</h3>
                <p>Ao apontar a câmera para a nota fiscal:</p>
                <ul>
                    <li><strong>Identificação Instantânea:</strong> O sistema reconhece os padrões <code>vNF</code> (valor) e <code>dhEmi</code> (data) dentro da URL.</li>
                    <li><strong>Bypass de Segurança:</strong> Os dados são extraídos mesmo que o site da SEFAZ esteja fora do ar ou pedindo validação humana.</li>
                    <li><strong>Preenchimento Automático:</strong> Em menos de 1 segundo, o valor e a data aparecem no seu formulário de despesas, prontos para salvar.</li>
                </ul>

                <h2>Por que isso é vital para o seu MEI?</h2>
                <p>Tempo é dinheiro. Se você gasta 2 minutos para lançar uma nota, ao final do mês, perdeu horas que poderiam ser usadas para vender mais. Com o lançamento via QR Code do Gestão MEI, você registra uma compra de insumos enquanto ainda está no balcão da loja.</p>
                
                <p>Além disso, o registro imediato evita que você perca o cupom fiscal (que costuma apagar com o calor) e garante que seu <strong>Livro Caixa</strong> esteja sempre 100% atualizado para a Declaração Anual.</p>

                <h2>Disponível para Todo o Brasil</h2>
                <p>Embora tenhamos focado em resolver os desafios específicos da SEFAZ-AP, essa tecnologia funciona com as notas fiscais de todos os estados brasileiros que seguem o padrão nacional de NFC-e. É a democratização da tecnologia de ponta para o pequeno empreendedor.</p>
            </>
        )
    },
    'livro-caixa-mei-ia': {
        title: "Como fazer o Livro Caixa do MEI em segundos usando Inteligência Artificial (OCR)",
        description: "Automatize seu livro caixa MEI com IA e OCR. Aprenda como guardar comprovantes e gerar relatórios automaticamente.",
        category: "Automação",
        date: "10 de Março, 2026",
        readTime: "4 min de leitura",
        image: "/relatorio mei .PNG",
        content: (
            <>
                <p>O maior pesadelo de todo Microempreendedor Individual (MEI) não é apenas vender, mas sim organizar a "papelada" que vem depois. Todo café, toda compra de insumo no Nubank e todo boleto pago formam uma montanha de dados que precisam estar no seu Livro Caixa.</p>
                
                <h2>O Diferencial: Adeus digitação manual</h2>
                <p>Antigamente, você precisava anotar valor por valor em uma planilha de Excel. Se perdesse o papel térmico que apaga com o tempo, perdia a dedução do seu lucro. Hoje, a <strong>Inteligência Artificial (OCR)</strong> mudou o jogo.</p>
                
                <h3>O que é o OCR e como ele ajuda o MEI?</h3>
                <p>OCR (Optical Character Recognition) é a tecnologia que permite que o sistema "leia" texto dentro de uma imagem. No Gestão MEI, você simplesmente tira uma foto do comprovante ou anexa o PDF do banco.</p>
                
                <ul>
                    <li><strong>Extração Automática:</strong> O sistema identifica Data, Valor e Categoria (Alimentação, Transporte, etc).</li>
                    <li><strong>Nubank & Bancos Digitais:</strong> Basta printar o comprovante e subir. A IA já reconhece o padrão.</li>
                    <li><strong>Organização Fiscal:</strong> Cada gasto é automaticamente abatido do seu lucro real, preparando o terreno para a sua declaração anual (DASN-SIMEI).</li>
                </ul>

                <h2>Como isso evita erros na Declaração Anual?</h2>
                <p>Muitos MEIs chegam em Maio sem saber quanto venderam e quanto gastaram, chutando valores na DASN. Isso é um convite para a malha fina. Com o Livro Caixa automatizado, você tem um PDF mastigado com todos os gastos dedutíveis, garantindo que você nunca pague mais imposto do que deveria.</p>
            </>
        )
    },
    'limite-mei-2026-radar': {
        title: "Limite do MEI em 2026: Como o Radar Preditivo evita que você seja desenquadrado por erro",
        description: "Saiba tudo sobre o limite de faturamento MEI em 2026 e como usar o Radar Preditivo para não ser desenquadrado.",
        category: "Segurança Fiscal",
        date: "08 de Março, 2026",
        readTime: "5 min de leitura",
        image: "/deshboard.PNG",
        content: (
            <>
                <p>O limite de faturamento do MEI é um dos assuntos mais sensíveis. Em 2026, com a inflação e o crescimento dos negócios digitais, muitos empreendedores correm o risco de ultrapassar os R$ 81.000,00 anuais sem perceber.</p>
                
                <h2>O perigo do desenquadramento retroativo</h2>
                <p>Se você ultrapassa o limite em mais de 20%, o desenquadramento é retroativo ao início do ano. Isso significa pagar impostos como Microempresa (ME) sobre tudo o que você já vendeu, além de multas e juros. É o fim de muitos negócios.</p>
                
                <h3>Radar Preditivo: Sua proteção 24/7</h3>
                <p>Nosso sistema não apenas soma o que você vendeu hoje. Ele utiliza um algoritmo de <strong>Inteligência Preditiva</strong> para analisar sua velocidade de crescimento.</p>
                
                <p>Imagine que você faturou R$ 8.000,00 em um mês. O sistema calcula: "Nesse ritmo, você atingirá o limite em Setembro". Você recebe um aviso visual no Dashboard, permitindo que você tome decisões estratégicas:</p>
                
                <ul>
                    <li>Pausar vendas no final do ano.</li>
                    <li>Planejar a transição para ME de forma estruturada.</li>
                    <li>Evitar a surpresa de uma notificação da Receita Federal.</li>
                </ul>

                <h2>Acompanhamento em Tempo Real</h2>
                <p>Com o gráfico de Projeção de Faturamento, você vê exatamente onde está e para onde seu CNPJ está indo. Não é apenas controle, é visão de longo prazo para quem quer crescer com segurança.</p>
            </>
        )
    },
    'simulador-irpf-mei-isencao': {
        title: "Simulador IRPF para MEI: Descubra quanto do seu lucro é isento de imposto de renda",
        description: "Use o simulador IRPF para MEI e descubra o seu lucro isento. Saiba como calcular pro labore e evitar impostos desnecessários.",
        category: "Economia",
        date: "05 de Março, 2026",
        readTime: "6 min de leitura",
        image: "/mobe.png",
        content: (
            <>
                <p>Uma das maiores dúvidas do MEI é: "Como eu passo o dinheiro da empresa para o meu CPF sem pagar Imposto de Renda?". A resposta está na regra da Parcela Isenta do Lucro Presumido.</p>
                
                <h2>Lucro vs. Rendimento Tributável</h2>
                <p>Nem tudo o que sobra na sua conta é isento. A Receita Federal define percentuais fixos de isenção dependendo da sua atividade:</p>
                
                <ul>
                    <li><strong>8%</strong> para comércio, indústria e transporte de carga.</li>
                    <li><strong>16%</strong> para transporte de passageiros.</li>
                    <li><strong>32%</strong> para prestação de serviços em geral.</li>
                </ul>

                <h3>Como o nosso Simulador funciona?</h3>
                <p>Dentro do Gestão MEI, nós automatizamos esse cálculo complexo. O sistema pega seu faturamento bruto, subtrai suas despesas comprovadas (aquelas do Livro Caixa que falamos antes) e aplica a regra de isenção.</p>
                
                <p>O resultado é dividido em duas colunas claras:</p>
                <ol>
                    <li><strong>Rendimento Isento:</strong> O valor que você informa na sua declaração de CPF sem pagar nada.</li>
                    <li><strong>Rendimento Tributável:</strong> O valor que, se ultrapassar o teto da Receita, pode gerar imposto.</li>
                </ol>

                <h2>Por que ter Pro-Labore organizado?</h2>
                <p>Ter esses valores documentados mês a mês é essencial para comprovar renda em bancos (conseguir empréstimos e cartões) e para estar 100% legalizado. O app já entrega o valor exato que você deve preencher no programa do IRPF, eliminando qualquer medo de errar na declaração.</p>
            </>
        )
    }
};

const BlogPost: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const post = slug ? posts[slug] : null;

    if (!post) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center text-white">
                <div className="text-center">
                    <h1 className="text-4xl font-black mb-4">Artigo não encontrado</h1>
                    <Link to="/blog" className="text-red-500 font-bold hover:underline">Voltar para o Blog</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white font-manrope selection:bg-red-500/30 selection:text-white pb-24">
            <SEO title={post.title} description={post.description} />

            {/* Progress Bar */}
            <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-white/5">
                <div className="h-full bg-red-600 shadow-[0_0_10px_rgba(220,38,38,0.5)] w-0" id="read-progress"></div>
            </div>

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0a]/80 backdrop-blur-md">
                <div className="container mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-rose-700 rounded-xl flex items-center justify-center">
                            <span className="font-black text-xs text-white">MEI</span>
                        </div>
                        <span className="text-xl font-bold tracking-tight">Gestão<span className="text-red-500">MEI</span></span>
                    </Link>
                    
                    <Link 
                        to="/blog"
                        className="text-white/40 hover:text-white text-xs font-black uppercase tracking-widest flex items-center gap-2 transition-all"
                    >
                        <ChevronLeft size={14} />
                        Todos os Artigos
                    </Link>
                </div>
            </header>

            <article className="pt-32">
                <div className="container mx-auto px-6">
                    {/* Intro */}
                    <div className="max-w-3xl mx-auto mb-16">
                        <div className="flex items-center gap-4 text-white/40 text-[10px] font-black uppercase tracking-[4px] mb-8">
                            <span className="px-3 py-1 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">{post.category}</span>
                            <span>•</span>
                            <div className="flex items-center gap-1.5">
                                <Clock size={12} />
                                {post.readTime}
                            </div>
                        </div>
                        
                        <h1 className="text-4xl md:text-6xl font-black mb-10 tracking-tighter leading-[1.1]">
                            {post.title}
                        </h1>

                        <div className="flex items-center justify-between py-6 border-y border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-rose-700 flex items-center justify-center font-black text-xs">GM</div>
                                <div>
                                    <p className="text-sm font-bold">Equipe Gestão MEI</p>
                                    <p className="text-[10px] text-white/40 font-black uppercase tracking-widest">{post.date}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-white/30">
                                <Share2 size={18} className="hover:text-white cursor-pointer transition-colors" />
                            </div>
                        </div>
                    </div>

                    {/* Content Body */}
                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-[40px] overflow-hidden border border-white/10 mb-16 shadow-2xl relative group">
                            <img src={post.image} alt={post.title} className="w-full aspect-video object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-700" />
                        </div>

                        <div className="prose prose-invert prose-red max-w-none 
                            prose-headings:font-black prose-headings:tracking-tighter prose-headings:text-white
                            prose-h2:text-3xl prose-h2:mt-16 prose-h2:mb-6
                            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-red-500
                            prose-p:text-white/60 prose-p:text-lg prose-p:leading-relaxed prose-p:mb-8
                            prose-strong:text-white prose-strong:font-black
                            prose-li:text-white/60 prose-li:text-lg prose-li:mb-2
                            prose-ul:mb-8
                        ">
                            {post.content}
                        </div>

                        {/* Article Footer / CTA */}
                        <div className="mt-24 p-12 bg-white/[0.03] border border-white/10 rounded-[48px] text-center relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/5 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2 group-hover:bg-red-600/10 transition-colors" />
                            
                            <div className="relative z-10">
                                <Zap className="text-red-500 mx-auto mb-6" size={40} />
                                <h3 className="text-3xl font-black mb-4">Pronto para o próximo nível?</h3>
                                <p className="text-white/40 mb-10 font-bold leading-relaxed max-w-md mx-auto italic text-lg">
                                    Pare de perder tempo com papelada. Deixe a nossa IA cuidar do seu Livro Caixa enquanto você foca em crescer.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                                    <Link 
                                        to="/auth"
                                        className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-[2px] transition-all hover:scale-105 active:scale-95 shadow-xl shadow-red-600/20 flex items-center justify-center gap-3"
                                    >
                                        Começar Grátis Agora
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>

                                <p className="mt-8 text-[10px] text-white/20 font-black uppercase tracking-[4px]">
                                    Sem cartão de crédito • Setup em 2 minutos
                                </p>
                            </div>
                        </div>

                        {/* Back to Blog */}
                        <div className="mt-16 text-center">
                            <Link 
                                to="/blog"
                                className="inline-flex items-center gap-2 text-white/30 hover:text-white font-bold transition-all group"
                            >
                                <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                                Voltar para o Blog
                            </Link>
                        </div>
                    </div>
                </div>
            </article>

            {/* Footer */}
            <footer className="mt-32 py-12 border-t border-white/5 text-center">
                <p className="text-white/20 text-[10px] font-black uppercase tracking-[8px]">
                    Gestão MEI Elite © 2026
                </p>
            </footer>
        </div>
    );
};

export default BlogPost;
